import { useState, useRef, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import {
  motion, AnimatePresence,
  useScroll, useTransform, useInView,
  useMotionValue, useSpring,
} from "framer-motion";

/* ═══════════════════════════════════════════
   THEME
═══════════════════════════════════════════ */
const C = {
  black:    "#070707",
  charcoal: "#0f0f0f",
  steel:    "#171717",
  panel:    "#1c1c1c",
  accent:   "#e8c84a",
  accentD:  "#b89a2a",
  blue:     "#4ab8e8",
  white:    "#f4f4ef",
  gray:     "#666",
  line:     "#242424",
  wa:       "#25D366",
};

/* ═══════════════════════════════════════════
   DATA
═══════════════════════════════════════════ */
const GALLERY = [
  { src:"https://images.unsplash.com/photo-1633014041037-f5446fb4ce99?q=80&w=774&auto=format&fit=crop", label:"Foam Wash Close-up" },
  { src:"https://images.unsplash.com/photo-1732357624591-f2137085659b?w=900&auto=format&fit=crop&q=60", label:"Pressure Spray" },
  { src:"https://plus.unsplash.com/premium_photo-1661377176676-f8c52c1c4b31?w=900&auto=format&fit=crop&q=60", label:"Hand Washing Detail" },
  { src:"https://images.unsplash.com/photo-1612766995016-3638f877af2f?w=900&auto=format&fit=crop&q=60", label:"Soap Foam Detail" },
  { src:"https://images.unsplash.com/photo-1708805283087-cb22474fb88e?w=900&auto=format&fit=crop&q=60", label:"Microfiber Wipe" },
  { src:"https://images.unsplash.com/photo-1651881857981-cba32332e06e?w=900&auto=format&fit=crop&q=60", label:"Wheel Cleaning" },
  { src:"https://images.unsplash.com/photo-1565689876115-23f4019e888d?w=900&auto=format&fit=crop&q=60", label:"Water Rinse Spray" },
  { src:"https://images.unsplash.com/photo-1567784430956-b42137db2920?w=900&auto=format&fit=crop&q=60", label:"Foam Cannon Spray" },
  { src:"https://images.unsplash.com/photo-1711668635787-59efac42f825?w=900&auto=format&fit=crop&q=60", label:"Detailing Hands Work" },
  { src:"https://images.unsplash.com/photo-1703609437109-8544f6bd54bd?w=900&auto=format&fit=crop&q=60", label:"Polishing Surface" },
  { src:"https://images.unsplash.com/photo-1705357311458-398681eb9da8?w=900&auto=format&fit=crop&q=60", label:"Drying With Cloth" },
  { src:"https://images.unsplash.com/photo-1761312834150-4beefff097a7?w=900&auto=format&fit=crop&q=60", label:"Shiny Wet Surface" },
];

const SERVICES = [
  {
    tag:"BASIC", title:"Basic Car Wash", time:"~30 mins", color:C.accent,
    img:"https://images.unsplash.com/photo-1607860108855-64acf2078ed9?w=600&q=80",
    desc:"The perfect regular maintenance wash. pH-neutral foam, quick vacuum, tyre polish — done in 30 minutes at your doorstep.",
    features:["Exterior Foam Wash (pH Neutral Shampoo)","Interior Vacuum","Tyre Polish"],
    pricing:[{type:"Hatchback",price:"₹499"},{type:"Sedan + SUV",price:"₹599"},{type:"Large SUV",price:"₹699"}],
  },
  {
    tag:"ELITE", title:"Elite Car Wash", time:"~1.5 hrs", color:C.blue,
    img:"https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80",
    desc:"A comprehensive deep clean from every angle — exterior pressure wash, two-bucket hand scrub, ceramic spray, and full interior detailing.",
    features:[
      "Wheel Clean & Polish",
      "Exterior Foam Wash with Pressure Washer (pH Neutral Shampoo)",
      "Two Bucket Hand Scrub (Wax Shampoo)",
      "Drying",
      "Ceramic Spray Coating on Exterior",
      "Windshield & Glass Clean",
      "Interior Clean & Polish (Seat, Dashboard, Boot, Floormat, Door)",
    ],
    pricing:[{type:"Hatchback",price:"₹1299"},{type:"Sedan + SUV",price:"₹1499"},{type:"Large SUV",price:"₹1699"}],
  },
  {
    tag:"PREMIUM", title:"Paint Correction & Wax Polish", time:"~3 hrs", color:"#aaa",
    img:"https://dmcarcare.in/wp-content/uploads/2024/07/DM-Car-Care-2-1024x1024.png",
    desc:"Our flagship service for serious car enthusiasts. Claybar treatment, 1-step paint correction and 2-step protection bring back showroom-level brilliance.",
    features:[
      "Wheel Clean & Polish",
      "Exterior Foam Wash with Pressure Washer (pH Neutral Shampoo)",
      "Two Bucket Hand Scrub (Wax Shampoo)",
      "Drying",
      "Ceramic Spray Coating on Exterior",
      "Windshield & Glass Clean",
      "Interior Clean & Polish (Seat, Dashboard, Boot, Floormat, Door)",
      "Claybar Treatment",
      "1 Step Paint Correction",
      "2 Step Paint Protection",
    ],
    pricing:[{type:"Hatchback",price:"₹3499"},{type:"Sedan + SUV",price:"₹3999"},{type:"Large SUV",price:"₹4499"}],
  },
];

const REVIEWS = [
  { name:"Rahul Sharma", location:"Salt Lake, Kolkata", rating:4.9, text:"Absolutely stunned by the Elite wash — my 5-year-old Swift looks factory-fresh. The ceramic spray coating made it water-repellent for weeks. Highly professional team!", car:"Maruti Swift", avatar:"RS" },
  { name:"Priya Chatterjee", location:"New Town, Kolkata", rating:5.0, text:"I was skeptical about doorstep service but KnockKnock completely blew me away. The interior detailing was immaculate — even cleared out dust I didn't know existed. Will book every month!", car:"Honda City", avatar:"PC" },
  { name:"Arijit Das", location:"Behala, Kolkata", rating:4.8, text:"Paint correction on my black Creta was transformative. The swirl marks are gone, the paint looks deep and glossy. Worth every rupee of the Premium package.", car:"Hyundai Creta", avatar:"AD" },
  { name:"Sneha Mukherjee", location:"Tollygunge, Kolkata", rating:5.0, text:"Booked via WhatsApp and they arrived right on time. The foam wash + wax shampoo combo left my car gleaming. No chemicals on the paint, totally safe. Love the professionalism!", car:"Tata Nexon", avatar:"SM" },
  { name:"Debashis Roy", location:"Ballygunge, Kolkata", rating:4.7, text:"Second time using their Elite package. Consistent quality every single time. The tyre polish and wheel cleaning make such a huge difference to the overall look. 10/10 service.", car:"Toyota Innova", avatar:"DR" },
  { name:"Ankita Sen", location:"Dum Dum, Kolkata", rating:4.9, text:"The 25% first-booking discount made it an amazing deal. But honestly even full price is worth it — the detailing is thorough, the staff courteous, and results are showroom-level.", car:"Kia Sonet", avatar:"AS" },
];

const WA_NUMBER = "919641915892";
const WA_MSG    = encodeURIComponent("Hi! I'd like to book a car wash with KnockKnock Car Care 🚗");

const SOCIAL = [
  { label:"Facebook",  href:"https://www.facebook.com/people/knock-knock-car-care/61587806250193/",  color:"#4267B2",
    icon:<svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg> },
  { label:"Instagram", href:"https://www.instagram.com/knockknockcar.care", color:"#E1306C",
    icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg> },
  { label:"YouTube",   href:"https://www.youtube.com/@knockknockcarcare",  color:"#FF0000",
    icon:<svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58 2.78 2.78 0 001.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.96A29 29 0 0023 12a29 29 0 00-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white"/></svg> },
  { label:"Website",   href:"https://www.knockknockcarcare.in",            color:C.accent,
    icon:<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg> },
];

/* ═══════════════════════════════════════════
   SEO HELPER
═══════════════════════════════════════════ */
function SEO({ title, description, keywords, url }) {
  const siteName = "KnockKnock Car Care";
  const fullTitle = `${title} | ${siteName}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Social Sharing */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url || "https://www.knockknockcarcare.in"} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={siteName} />
      {/* Assuming logo.png is your fallback sharing image */}
      <meta property="og:image" content="https://www.knockknockcarcare.in/logo.png" /> 

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
}

/* ═══════════════════════════════════════════
   SHARED HELPERS
═══════════════════════════════════════════ */
function FadeIn({ children, delay=0, y=52, x=0, className="" }) {
  const ref = useRef(null);
  const ok  = useInView(ref, { once:true, margin:"-70px" });
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity:0, y, x }}
      animate={ok ? { opacity:1, y:0, x:0 } : {}}
      transition={{ duration:.9, delay, ease:[.22,1,.36,1] }}>
      {children}
    </motion.div>
  );
}

function StaggerList({ children, delay=0 }) {
  const ref = useRef(null);
  const ok  = useInView(ref, { once:true, margin:"-60px" });
  return (
    <motion.ul ref={ref} style={{ listStyle:"none" }}
      initial="hidden" animate={ok?"show":"hidden"}
      variants={{ hidden:{}, show:{ transition:{ staggerChildren:.08, delayChildren:delay }}}}>
      {children}
    </motion.ul>
  );
}

function StaggerItem({ children, style={} }) {
  return (
    <motion.li style={style}
      variants={{ hidden:{ opacity:0, x:-20 }, show:{ opacity:1, x:0, transition:{ duration:.6, ease:[.22,1,.36,1] }}}}>
      {children}
    </motion.li>
  );
}

function Counter({ to }) {
  const ref = useRef(null);
  const ok  = useInView(ref, { once:true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!ok) return;
    let start = 0;
    const end  = parseInt(String(to).replace(/\D/g,"")) || 0;
    if (!end) { setVal(to); return; }
    const step  = Math.ceil(end / 40);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setVal(to); clearInterval(timer); }
      else setVal(String(to).replace(/\d+/, start));
    }, 35);
    return () => clearInterval(timer);
  }, [ok, to]);
  return <span ref={ref}>{val}</span>;
}

function Stars({ rating, color="#e8c84a", size=16 }) {
  return (
    <div style={{ display:"flex", gap:2, alignItems:"center" }}>
      {[1,2,3,4,5].map(i => {
        const fill = Math.min(1, Math.max(0, rating - (i - 1)));
        const uid  = `sg-${i}-${String(rating).replace(".","x")}`;
        return (
          <svg key={i} viewBox="0 0 20 20" width={size} height={size}>
            <defs>
              <linearGradient id={uid} x1="0" x2="1" y1="0" y2="0">
                <stop offset={`${fill*100}%`} stopColor={color}/>
                <stop offset={`${fill*100}%`} stopColor="rgba(255,255,255,0.12)"/>
              </linearGradient>
            </defs>
            <path d="M10 1l2.39 5.26L18 7.27l-4 3.9.94 5.5L10 14.1l-4.94 2.57.94-5.5-4-3.9 5.61-.51z"
              fill={`url(#${uid})`}/>
          </svg>
        );
      })}
    </div>
  );
}

function PageBanner({ title, sub, accent }) {
  return (
    <div style={{ paddingTop:120, paddingBottom:60, textAlign:"center",
                  background:`linear-gradient(170deg,${C.charcoal} 0%,${C.black} 100%)`,
                  borderBottom:`1px solid ${C.line}`, position:"relative", overflow:"hidden" }}>
      <motion.div
        animate={{ x:["-10%","110%"] }}
        transition={{ repeat:Infinity, duration:6, ease:"easeInOut", repeatDelay:5 }}
        style={{ position:"absolute", inset:0, width:3,
                 background:`linear-gradient(to bottom,transparent,rgba(232,200,74,.2),transparent)`,
                 filter:"blur(4px)" }} />
      <motion.p className="dm"
        initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} transition={{ delay:.1 }}
        style={{ fontSize:10, letterSpacing:7, color:C.accent, textTransform:"uppercase", marginBottom:10 }}>
        {accent}
      </motion.p>
      <motion.h1 className="bb"
        initial={{ opacity:0, y:28 }} animate={{ opacity:1, y:0 }} transition={{ delay:.2, ease:[.22,1,.36,1] }}
        style={{ fontSize:"clamp(52px,9vw,110px)", letterSpacing:3, lineHeight:.9 }}>
        {title}
      </motion.h1>
      {sub && (
        <motion.p className="dm"
          initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:.32 }}
          style={{ fontSize:16, color:C.gray, marginTop:14, fontWeight:300, fontStyle:"italic" }}>
          {sub}
        </motion.p>
      )}
      <motion.div
        initial={{ scaleX:0 }} animate={{ scaleX:1 }} transition={{ duration:1, delay:.4 }}
        style={{ height:1, background:`linear-gradient(to right,transparent,${C.accent},transparent)`,
                 width:180, margin:"24px auto 0" }} />
    </div>
  );
}

function BookCTA() {
  const navigate = useNavigate();
  return (
    <FadeIn>
      <div className="sec" style={{ padding:"80px 52px" }}>
        <motion.div
          whileHover={{ scale:1.01 }}
          style={{
            maxWidth:1100, margin:"0 auto",
            background:`linear-gradient(135deg,#1a1100 0%,#0f0a00 100%)`,
            border:`1px solid rgba(232,200,74,.22)`, borderRadius:8,
            padding:"52px", display:"flex", alignItems:"center",
            justifyContent:"space-between", flexWrap:"wrap", gap:28,
            position:"relative", overflow:"hidden",
          }}>
          <div style={{ position:"absolute", top:-60, right:-60, width:260, height:260,
                        borderRadius:"50%", background:"rgba(232,200,74,.06)", filter:"blur(50px)" }} />
          <div>
            <div className="dm" style={{ fontSize:10, letterSpacing:5, color:C.accent,
                                         textTransform:"uppercase", marginBottom:10 }}>Ready?</div>
            <h3 className="bb" style={{ fontSize:"clamp(28px,4vw,52px)", lineHeight:1.05 }}>
              Book your wash today.<br />
              {/* <span style={{ color:C.accent }}>25% OFF</span> your first booking. */}
            </h3>
          </div>
          <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
            <motion.button
              whileHover={{ scale:1.06, boxShadow:`0 0 32px rgba(232,200,74,.5)` }}
              whileTap={{ scale:.95 }}
              onClick={() => navigate("/contact")}
              style={{
                background:C.accent, color:C.black, padding:"16px 36px",
                fontSize:11, letterSpacing:4, fontFamily:"'DM Sans',sans-serif",
                fontWeight:700, borderRadius:2, border:"none", cursor:"pointer", textTransform:"uppercase",
              }}>
              Book Now
            </motion.button>
            <motion.a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`} target="_blank" rel="noreferrer"
              whileHover={{ scale:1.06, boxShadow:"0 0 32px rgba(37,211,102,.4)" }}
              style={{
                display:"inline-flex", alignItems:"center", gap:10,
                background:C.wa, color:"#fff", padding:"16px 28px",
                fontSize:11, letterSpacing:3, fontFamily:"'DM Sans',sans-serif",
                fontWeight:700, borderRadius:2, textTransform:"uppercase",
              }}>
              <svg viewBox="0 0 32 32" fill="white" width="16" height="16">
                <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.676 4.612 1.843 6.504L4 29l7.697-1.82A11.93 11.93 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3z"/>
              </svg>
              WhatsApp
            </motion.a>
          </div>
        </motion.div>
      </div>
    </FadeIn>
  );
}

function CursorGlow() {
  const mx = useMotionValue(-200), my = useMotionValue(-200);
  const sx = useSpring(mx, { stiffness:120, damping:22 });
  const sy = useSpring(my, { stiffness:120, damping:22 });
  useEffect(() => {
    const fn = e => { mx.set(e.clientX); my.set(e.clientY); };
    window.addEventListener("mousemove", fn);
    return () => window.removeEventListener("mousemove", fn);
  }, []);
  return (
    <motion.div style={{
      position:"fixed", top:0, left:0, width:420, height:420,
      borderRadius:"50%", pointerEvents:"none", zIndex:0,
      background:"radial-gradient(circle, rgba(232,200,74,.05) 0%, transparent 70%)",
      translateX:sx, translateY:sy, transform:"translate(-50%,-50%)",
    }} />
  );
}

/* ═══════════════════════════════════════════
   NAV
═══════════════════════════════════════════ */
function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [waTooltip, setWaTooltip] = useState(false);
  const { scrollY } = useScroll();
  const navBg = useTransform(scrollY, [0,80], ["rgba(7,7,7,0)","rgba(7,7,7,0.97)"]);
  const location = useLocation();
  const navigate = useNavigate();

  const NAV_LINKS = [
    { label:"Home",     path:"/" },
    { label:"About",    path:"/about" },
    { label:"Services", path:"/services" },
    { label:"Pricing",  path:"/pricing" },
    { label:"Gallery",  path:"/gallery" },
    { label:"Reviews",  path:"/reviews" },
    { label:"Contact",  path:"/contact" },
  ];

  return (
    <>
      <motion.nav
        initial={{ y:-80 }} animate={{ y:0 }}
        transition={{ duration:.75, ease:[.22,1,.36,1] }}
        className="sec"
        style={{
          position:"fixed", top:0, left:0, right:0, zIndex:100,
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"16px 52px", background:navBg,
        }}>
        <motion.div className="bb" style={{ lineHeight:1, cursor:"pointer" }}
          whileHover={{ scale:1.04 }} onClick={() => navigate("/")}>
          <div style={{ fontSize:20, letterSpacing:3 }}>
            <span style={{ color:C.accent }}>KNOCK!</span><span style={{ color:C.white }}>KNOCK!</span>
          </div>
          <div className="dm" style={{ fontSize:7, letterSpacing:5, color:C.gray, fontWeight:300 }}>
            CAR CARE · YOUR CAR, OUR CARE
          </div>
        </motion.div>

        <div className="dl" style={{ display:"flex", gap:24, alignItems:"center" }}>
          {NAV_LINKS.map((l,i) => (
            <motion.div key={l.path}
              initial={{ opacity:0, y:-12 }} animate={{ opacity:.8, y:0 }}
              transition={{ delay:.06*i }}>
              <Link to={l.path}
                className="nb"
                style={{ color: location.pathname===l.path ? C.accent : C.white,
                         opacity: location.pathname===l.path ? 1 : undefined,
                         borderBottom: location.pathname===l.path ? `1px solid ${C.accent}` : undefined }}>
                {l.label}
              </Link>
            </motion.div>
          ))}
          <motion.a href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`} target="_blank" rel="noreferrer"
            whileHover={{ scale:1.06 }} whileTap={{ scale:.95 }}
            onMouseEnter={() => setWaTooltip(true)} onMouseLeave={() => setWaTooltip(false)}
            style={{ background:C.wa, color:"#fff", padding:"9px 16px",
                     fontSize:10, letterSpacing:1.5, fontFamily:"'DM Sans',sans-serif",
                     fontWeight:600, borderRadius:2, display:"flex", alignItems:"center", gap:6,
                     position:"relative" }}>
            <svg viewBox="0 0 32 32" fill="white" width="13" height="13">
              <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.676 4.612 1.843 6.504L4 29l7.697-1.82A11.93 11.93 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3z"/>
            </svg>
            WhatsApp
          </motion.a>
          <motion.a href="tel:9641915892"
            whileHover={{ scale:1.06 }} whileTap={{ scale:.95 }}
            style={{ background:C.accent, color:C.black, padding:"9px 16px",
                     fontSize:10, letterSpacing:1.5, fontFamily:"'DM Sans',sans-serif", fontWeight:600, borderRadius:2 }}>
            📞 9641915892
          </motion.a>
        </div>

        <button className="hm" onClick={() => setMenuOpen(v => !v)}
          style={{ background:"none", flexDirection:"column", gap:5, padding:4 }}>
          {[0,1,2].map(i => (
            <motion.div key={i}
              animate={menuOpen
                ? { rotate:i===1?0:i===0?45:-45, y:i===1?0:i===0?9:-9, opacity:i===1?0:1 }
                : { rotate:0, y:0, opacity:1 }}
              transition={{ duration:.3 }}
              style={{ width:24, height:2, background:C.white, borderRadius:2 }} />
          ))}
        </button>
      </motion.nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ clipPath:"circle(0% at 95% 5%)" }}
            animate={{ clipPath:"circle(160% at 95% 5%)" }}
            exit={{ clipPath:"circle(0% at 95% 5%)" }}
            transition={{ duration:.55, ease:[.22,1,.36,1] }}
            style={{
              position:"fixed", inset:0, zIndex:99, background:C.charcoal,
              display:"flex", flexDirection:"column", alignItems:"center",
              justifyContent:"center", gap:20, overflowY:"auto", paddingTop:80,
            }}>
            {NAV_LINKS.map((l,i) => (
              <motion.div key={l.path}
                initial={{ opacity:0, y:32 }} animate={{ opacity:1, y:0 }}
                transition={{ delay:i*.06+.1 }}>
                <Link to={l.path} className="bb"
                  style={{ fontSize:46, color: location.pathname===l.path ? C.accent : C.white,
                           letterSpacing:3, display:"block" }}
                  onClick={() => setMenuOpen(false)}>
                  {l.label.toUpperCase()}
                </Link>
              </motion.div>
            ))}
            <motion.a href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`} target="_blank" rel="noreferrer"
              initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:.55 }}
              style={{ fontFamily:"'DM Sans',sans-serif", fontSize:16, color:C.wa, letterSpacing:2, marginTop:12 }}>
              💬 WhatsApp Us
            </motion.a>
            <motion.a href="tel:9641915892"
              initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:.62 }}
              style={{ fontFamily:"'DM Sans',sans-serif", fontSize:16, color:C.accent, letterSpacing:2 }}>
              📞 9641915892
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ═══════════════════════════════════════════
   FOOTER
═══════════════════════════════════════════ */
function Footer() {
  const navigate = useNavigate();
  return (
    <footer className="sec" style={{ background:C.black, borderTop:`1px solid ${C.line}`, padding:"56px 52px" }}>
      <div style={{ maxWidth:1200, margin:"0 auto" }}>
        <div className="fg"
          style={{ display:"grid", gridTemplateColumns:"1.5fr 1fr 1.2fr 1.5fr", gap:44, marginBottom:48 }}>
          
          {/* Column 1: Logo & Contact */}
          <div>
            <motion.div className="bb" whileHover={{ scale:1.03 }}
              style={{ cursor:"pointer", display:"inline-block" }}
              onClick={() => navigate("/")}>
              <img src="/logo.png" alt="KnockKnock Car Care" style={{ height: 65, objectFit: "contain" }} />
            </motion.div>
            <p className="dm" style={{ fontSize:14, color:C.gray, fontWeight:300,
                                       marginTop:14, lineHeight:1.75, maxWidth:250, fontStyle:"italic" }}>
              "Your car, our care."<br />Premium doorstep detailing, at your convenience.
            </p>
            <div style={{ marginTop:20, display:"flex", flexDirection:"column", gap:12 }}>
              <a href="tel:9641915892" className="dm" style={{ fontSize:13, color:"rgba(244,244,239,.55)", display: "flex", alignItems: "center", gap: 8 }}>📞 9641915892</a>
              <a href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`} target="_blank" rel="noreferrer" className="dm"
                style={{ fontSize:13, color:C.wa, display: "flex", alignItems: "center", gap: 8 }}>💬 WhatsApp Us</a>
              <a href="https://www.knockknockcarcare.in" target="_blank" rel="noreferrer" className="dm"
                style={{ fontSize:13, color:"rgba(244,244,239,.55)", display: "flex", alignItems: "center", gap: 8 }}>🌐 knockknockcarcare.in</a>
            </div>
            <div style={{ marginTop:24, display:"flex", gap:10 }}>
              {SOCIAL.map(s => (
                <motion.a key={s.label} href={s.href} target="_blank" rel="noreferrer"
                  whileHover={{ scale:1.15, backgroundColor:s.color, borderColor:s.color, color:"#fff" }}
                  title={s.label}
                  style={{ width:38, height:38, borderRadius:4, display:"flex", alignItems:"center",
                           justifyContent:"center", border:`1px solid ${C.line}`, color:C.gray, transition:"all .25s" }}>
                  {s.icon}
                </motion.a>
              ))}
            </div>
          </div>

          {/* Column 2: Navigate */}
          <div>
            <p className="dm" style={{ fontSize:10, letterSpacing:4, color:C.gray, textTransform:"uppercase", marginBottom:20 }}>Navigate</p>
            {[
              ["HOME","/"],
              ["ABOUT","/about"],
              ["SERVICES","/services"],
              ["PRICING","/pricing"],
              ["GALLERY","/gallery"],
              ["REVIEWS","/reviews"],
              ["CONTACT","/contact"],
              ["PRIVACY POLICY","/privacy-policy"],
              ["DISCLAIMER","/disclaimer"]
            ].map(([l,p]) => (
              <Link key={p} to={p} className="nb"
                style={{ display:"block", marginBottom:14, fontSize:11, letterSpacing:1.5, color:"rgba(244,244,239,.55)" }}>{l}</Link>
            ))}
          </div>

          {/* Column 3: Top Services */}
          <div>
            <p className="dm" style={{ fontSize:10, letterSpacing:4, color:C.gray, textTransform:"uppercase", marginBottom:20 }}>Top Services</p>
            {[
              "Basic Doorstep Wash",
              "Elite Car Wash & Vacuum",
              "Paint Correction & Polish",
              "Ceramic Spray Coating"
            ].map(s => (
              <p key={s} className="dm" style={{ fontSize:13, color:"rgba(244,244,239,.42)", fontWeight:300, marginBottom:14 }}>{s}</p>
            ))}
          </div>

          {/* Column 4: Areas We Serve */}
          <div>
          <Link to="/contact">  <p className="dm" style={{ fontSize:10, letterSpacing:4, color:C.gray, textTransform:"uppercase", marginBottom:20 }}>Areas We Serve</p>
            {[
              "CAR CLEANING IN KOLKATA",
              "SALT LAKE TECH DETAILING",
              "NEW TOWN DOORSTEP WASH",
              "DUM DUM AUTO SPA",
              "RAJARHAT CAR CLEANING",
              "BIDHANNAGAR WASH SERVICE"
            ].map(s => (
              <p key={s} className="dm" style={{ fontSize:11, letterSpacing:1.5, color:C.accent, fontWeight:400, marginBottom:14 }}>{s}</p>
            ))}
            </Link>
          </div>
        </div>

        {/* Bottom Bar */}
        <div style={{ borderTop:`1px solid ${C.line}`, paddingTop:24,
                      display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
          <p className="dm" style={{ fontSize:12, color:C.gray }}>© 2025 KnockKnock Car Care. All rights reserved.</p>
          <p className="dm" style={{ fontSize:12, color:C.gray }}>Made with ❤️ for car lovers · knockknockcarcare.in</p>
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════
   FLOATING WA BUTTON
═══════════════════════════════════════════ */
function FloatingWA() {
  const [tip, setTip] = useState(false);
  return (
    <motion.div
      initial={{ scale:0, opacity:0 }} animate={{ scale:1, opacity:1 }}
      transition={{ delay:2, type:"spring", stiffness:260, damping:18 }}
      style={{ position:"fixed", bottom:28, right:28, zIndex:300 }}>
      <AnimatePresence>
        {tip && (
          <motion.div initial={{ opacity:0, x:16 }} animate={{ opacity:1, x:0 }} exit={{ opacity:0, x:16 }}
            style={{ position:"absolute", right:64, top:"50%", transform:"translateY(-50%)",
                     background:C.wa, color:"#fff", padding:"8px 16px", borderRadius:24,
                     whiteSpace:"nowrap", fontFamily:"'DM Sans',sans-serif", fontSize:13, fontWeight:600,
                     boxShadow:"0 4px 20px rgba(37,211,102,.4)" }}>
            Chat with us on WhatsApp!
            <div style={{ position:"absolute", right:-7, top:"50%", transform:"translateY(-50%)",
                          width:0, height:0, borderTop:"7px solid transparent",
                          borderBottom:"7px solid transparent", borderLeft:`7px solid ${C.wa}` }}/>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.a
        href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`} target="_blank" rel="noreferrer"
        whileHover={{ scale:1.15 }} whileTap={{ scale:.92 }}
        onMouseEnter={() => setTip(true)} onMouseLeave={() => setTip(false)}
        animate={{ y:[0,-6,0] }} transition={{ y:{ repeat:Infinity, duration:2.4, ease:"easeInOut" } }}
        style={{ display:"flex", alignItems:"center", justifyContent:"center",
                 width:56, height:56, borderRadius:"50%", background:C.wa,
                 boxShadow:"0 6px 28px rgba(37,211,102,.55)" }}>
        <svg viewBox="0 0 32 32" fill="white" width="30" height="30">
          <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.676 4.612 1.843 6.504L4 29l7.697-1.82A11.93 11.93 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3zm0 22c-1.97 0-3.82-.535-5.41-1.46l-.387-.23-4.572 1.08 1.103-4.453-.253-.4A9.96 9.96 0 016 15c0-5.514 4.486-10 10-10s10 4.486 10 10-4.486 10-10 10zm5.36-7.47c-.294-.147-1.74-.858-2.01-.956-.27-.097-.466-.146-.662.147-.196.294-.76.956-.931 1.153-.171.196-.343.22-.637.073-.294-.147-1.24-.457-2.362-1.456-.873-.778-1.462-1.739-1.634-2.033-.171-.294-.018-.453.129-.6.132-.131.294-.343.44-.514.147-.172.196-.294.294-.49.098-.196.049-.367-.024-.514-.073-.147-.662-1.596-.907-2.185-.239-.574-.482-.496-.662-.505l-.563-.01c-.196 0-.514.073-.783.367s-1.028 1.005-1.028 2.45 1.053 2.843 1.2 3.04c.147.196 2.072 3.164 5.02 4.437.702.303 1.25.484 1.677.62.705.224 1.346.193 1.853.117.565-.084 1.74-.711 1.985-1.398.245-.687.245-1.276.171-1.398-.073-.123-.27-.196-.563-.343z"/>
        </svg>
      </motion.a>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════
   PAGE: HOME
═══════════════════════════════════════════ */
function PageHome() {
  const videoRef = useRef(null);
  const { scrollY } = useScroll();
  const heroParallax = useTransform(scrollY, [0,700], [0,200]);
  const heroOpacity  = useTransform(scrollY, [0,420], [1,0]);
  const navigate = useNavigate();
  const reviewTimer = useRef(null);
  const [reviewIdx, setReviewIdx] = useState(0);

  useEffect(() => { videoRef.current?.play().catch(()=>{}); }, []);
  useEffect(() => {
    reviewTimer.current = setInterval(() => setReviewIdx(i => (i+1)%REVIEWS.length), 4200);
    return () => clearInterval(reviewTimer.current);
  }, []);

  return (
    <>
      <SEO 
        title="Premium Doorstep Car Wash & Detailing in Kolkata" 
        description="Experience premium doorstep car detailing, washing, paint correction, and ceramic coating in Kolkata, Salt Lake, New Town, and Rajarhat. Book KnockKnock Car Care today." 
        keywords="car cleaning in Kolkata, salt lake tech detailing, new town doorstep wash, dum dum auto spa, rajarhat car cleaning, door step car wash" 
        url="https://www.knockknockcarcare.in/" 
      />
      {/* HERO */}
      <section style={{ position:"relative", height:"100vh", overflow:"hidden",
                        display:"flex", alignItems:"center", justifyContent:"center" }}>
        <motion.div style={{ position:"absolute", inset:0, y:heroParallax }}>
          <video ref={videoRef} autoPlay muted loop playsInline
            style={{ width:"100%", height:"100%", objectFit:"cover" }}>
            <source src="https://assets.mixkit.co/videos/45755/45755-720.mp4" type="video/mp4" />
          </video>
          <div style={{ position:"absolute", inset:0,
            background:"linear-gradient(170deg,rgba(7,7,7,.45) 0%,rgba(7,7,7,.72) 50%,rgba(7,7,7,1) 100%)" }} />
          <motion.div animate={{ x:["-10%","110%"] }}
            transition={{ repeat:Infinity, duration:5, ease:"easeInOut", repeatDelay:4 }}
            style={{ position:"absolute", inset:0, width:3,
                     background:"linear-gradient(to bottom,transparent,rgba(232,200,74,.25),transparent)",
                     filter:"blur(4px)" }} />
        </motion.div>

        <motion.div style={{ position:"relative", textAlign:"center", padding:"0 24px", opacity:heroOpacity }}>
          <motion.div initial={{ scaleX:0 }} animate={{ scaleX:1 }}
            transition={{ duration:1.2, delay:.2 }}
            style={{ height:1, background:`linear-gradient(to right,transparent,${C.accent},transparent)`,
                     width:200, margin:"0 auto 30px" }} />
          <motion.p className="dm"
            initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
            transition={{ delay:.38 }}
            style={{ fontSize:11, letterSpacing:7, color:C.accent, textTransform:"uppercase", marginBottom:24 }}>
            Premium Doorstep Car Wash
          </motion.p>
          {["KNOCK!", "KNOCK!"].map((word, wi) => (
            <div key={wi} className="bb"
              style={{ display:"block", fontSize:"clamp(60px,13vw,150px)", lineHeight:.86, letterSpacing:4 }}>
              {word.split("").map((ch, ci) => (
                <motion.span key={ci}
                  initial={{ opacity:0, y:60 }} animate={{ opacity:1, y:0 }}
                  transition={{ delay:.5+wi*.18+ci*.04, duration:.7, ease:[.22,1,.36,1] }}
                  style={{ display:"inline-block",
                           color: wi===1 ? "transparent" : C.white,
                           WebkitTextStroke: wi===1 ? `2px ${C.accent}` : "none" }}>
                  {ch}
                </motion.span>
              ))}
            </div>
          ))}
          <motion.div className="bb"
            initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:1.05 }}
            style={{ fontSize:"clamp(20px,4vw,48px)", letterSpacing:12,
                     color:"rgba(244,244,239,.4)", marginTop:4, marginBottom:28 }}>
            CAR CARE
          </motion.div>
          <motion.p className="dm"
            initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:1.15 }}
            style={{ fontSize:17, color:"rgba(244,244,239,.6)", maxWidth:460, margin:"0 auto 20px",
                     lineHeight:1.8, fontWeight:300, fontStyle:"italic" }}>
            "Because your car deserves premium care."
          </motion.p>
          <motion.div initial={{ opacity:0, scale:.8 }} animate={{ opacity:1, scale:1 }}
            transition={{ delay:1.28, type:"spring" }}
            style={{ display:"inline-flex", alignItems:"center", gap:10, marginBottom:48,
                     background:"rgba(232,200,74,.1)", border:`1px solid rgba(232,200,74,.38)`,
                     padding:"10px 24px", borderRadius:24 }}>
            <motion.span animate={{ rotate:[0,20,0,-20,0] }} transition={{ repeat:Infinity, duration:2, delay:2 }}>🎉</motion.span>
            <span className="dm" style={{ fontSize:13, color:C.accent, fontWeight:600, letterSpacing:.8 }}>
              Starting from ₹499 
            </span>
          </motion.div>
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:1.38 }}
            style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap" }}>
            <motion.button whileHover={{ scale:1.06, boxShadow:`0 0 36px rgba(232,200,74,.5)` }}
              whileTap={{ scale:.95 }} onClick={() => navigate("/contact")}
              style={{ background:C.accent, color:C.black, padding:"16px 48px",
                       fontSize:11, letterSpacing:4, fontFamily:"'DM Sans',sans-serif",
                       fontWeight:700, borderRadius:2, textTransform:"uppercase", border:"none", cursor:"pointer" }}>
              Book Now
            </motion.button>
            <motion.a href={`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`} target="_blank" rel="noreferrer"
              whileHover={{ scale:1.06 }}
              style={{ display:"inline-flex", alignItems:"center", gap:9,
                       background:C.wa, color:"#fff", padding:"16px 36px",
                       fontSize:11, letterSpacing:3, fontFamily:"'DM Sans',sans-serif",
                       fontWeight:700, borderRadius:2, textTransform:"uppercase" }}>
              <svg viewBox="0 0 32 32" fill="white" width="16" height="16">
                <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.676 4.612 1.843 6.504L4 29l7.697-1.82A11.93 11.93 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3z"/>
              </svg>
              WhatsApp
            </motion.a>
          </motion.div>
          <motion.div initial={{ scaleX:0 }} animate={{ scaleX:1 }} transition={{ duration:1.2, delay:.2 }}
            style={{ height:1, background:`linear-gradient(to right,transparent,${C.accent},transparent)`,
                     width:200, margin:"44px auto 0" }} />
        </motion.div>

        <motion.div animate={{ y:[0,10,0] }} transition={{ repeat:Infinity, duration:2.2 }}
          style={{ position:"absolute", bottom:26, left:"50%", transform:"translateX(-50%)",
                   display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
          <div style={{ width:1, height:52, background:`linear-gradient(to bottom,transparent,${C.accent})` }} />
          <span className="dm" style={{ fontSize:8, letterSpacing:5, color:C.gray, textTransform:"uppercase" }}>Scroll</span>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div style={{ overflow:"hidden", background:C.accent, padding:"12px 0" }}>
        <motion.div animate={{ x:[0,"-50%"] }} transition={{ repeat:Infinity, duration:18, ease:"linear" }}
          style={{ display:"flex", whiteSpace:"nowrap" }}>
          {[...Array(2)].map((_,ri) => (
            <span key={ri} className="bb" style={{ fontSize:14, letterSpacing:4, color:C.black }}>
              {["DOORSTEP SERVICE","·","BASIC WASH ₹499","·","ELITE WASH ₹1299","·",
               "CERAMIC SPRAY COATING","·","pH NEUTRAL SHAMPOO","·",
                "NO HIDDEN CHARGES","·","CALL 9641915892","·"].map((t,i) => (
                <span key={i} style={{ paddingRight:32 }}>{t}</span>
              ))}
            </span>
          ))}
        </motion.div>
      </div>

      {/* ABOUT PREVIEW */}
      <section className="sec" style={{ padding:"100px 52px" }}>
        <div className="ag" style={{ maxWidth:1200, margin:"0 auto", display:"grid",
                                     gridTemplateColumns:"1fr 1fr", gap:80, alignItems:"center" }}>
          <div>
            <FadeIn>
              <p className="dm" style={{ fontSize:10, letterSpacing:6, color:C.accent, textTransform:"uppercase", marginBottom:14 }}>Who We Are</p>
              <h2 className="bb" style={{ fontSize:"clamp(42px,6vw,78px)", lineHeight:.92, letterSpacing:2, marginBottom:24 }}>
                YOUR CAR,<br /><span style={{ background:`linear-gradient(135deg,${C.accent} 0%,#fff5b4 50%,${C.accentD} 100%)`,
                  WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" }}>OUR CARE.</span>
              </h2>
              <p className="dm" style={{ fontSize:16, lineHeight:1.9, color:"rgba(244,244,239,.58)", fontWeight:300, marginBottom:28 }}>
                KnockKnock Car Care is a <strong style={{ color:C.white }}>premium doorstep car wash</strong> service.
                We come fully loaded — professional-grade equipment and pH-neutral products, straight to your home or office.
              </p>
              <div style={{ display:"flex", gap:44, flexWrap:"wrap", marginBottom:36 }}>
                {[["₹499","Starting Price"],["30 min","Quick Wash"]].map(([n,l]) => (
                  <div key={l}>
                    <div className="bb" style={{ fontSize:40, color:C.accent, lineHeight:1 }}><Counter to={n} /></div>
                    <div className="dm" style={{ fontSize:10, color:C.gray, letterSpacing:3, textTransform:"uppercase", marginTop:4 }}>{l}</div>
                  </div>
                ))}
              </div>
              <motion.button whileHover={{ scale:1.05 }} whileTap={{ scale:.95 }}
                onClick={() => navigate("/about")}
                style={{ background:"none", border:`1px solid ${C.accent}`, color:C.accent,
                         padding:"12px 36px", fontSize:10, letterSpacing:4,
                         fontFamily:"'DM Sans',sans-serif", borderRadius:2,
                         textTransform:"uppercase", cursor:"pointer" }}>
                Our Story →
              </motion.button>
            </FadeIn>
          </div>
          <FadeIn delay={.2} x={40}>
            <motion.div whileHover={{ scale:1.02 }} transition={{ type:"spring", stiffness:200 }}
              style={{ borderRadius:4, overflow:"hidden",
                       boxShadow:`22px 22px 60px rgba(0,0,0,.8), -3px -3px 0 ${C.accent}`, position:"relative" }}>
              <img src="https://images.unsplash.com/photo-1607860108855-64acf2078ed9?w=900&q=85"
                alt="Car foam wash" style={{ width:"100%", height:480, objectFit:"cover", display:"block" }} />
              <motion.div initial={{ scale:0, rotate:-15 }} whileInView={{ scale:1, rotate:0 }}
                transition={{ type:"spring", damping:12, delay:.3 }} viewport={{ once:true }}
                style={{ position:"absolute", bottom:-20, right:-20,
                         background:C.accent, color:C.black, padding:"16px 20px", borderRadius:3 }}>
                <div className="bb" style={{ fontSize:28, lineHeight:1 }}>100%</div>
                <div className="dm" style={{ fontSize:9, letterSpacing:2, fontWeight:600 }}>SATISFACTION</div>
              </motion.div>
            </motion.div>
          </FadeIn>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="sec" style={{ padding:"80px 52px", background:C.charcoal }}>
        <FadeIn>
          <div style={{ textAlign:"center", marginBottom:60 }}>
            <p className="dm" style={{ fontSize:10, letterSpacing:6, color:C.accent, textTransform:"uppercase", marginBottom:12 }}>What We Offer</p>
            <h2 className="bb" style={{ fontSize:"clamp(42px,6vw,78px)", letterSpacing:2 }}>OUR SERVICES</h2>
          </div>
        </FadeIn>
        <div className="sg" style={{ maxWidth:1100, margin:"0 auto", display:"grid",
                                     gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:24 }}>
          {SERVICES.map((svc,si) => (
            <FadeIn key={svc.tag} delay={si*.12} y={50}>
              <motion.div whileHover={{ y:-8 }} transition={{ type:"spring", stiffness:200 }}
                style={{ background:C.steel, border:`1px solid ${C.line}`, borderRadius:6, overflow:"hidden" }}>
                <div style={{ position:"relative", height:170, overflow:"hidden" }}>
                  <img src={svc.img} alt={svc.title}
                    style={{ width:"100%", height:"100%", objectFit:"cover", filter:"brightness(.55)" }} />
                  <div style={{ position:"absolute", inset:0,
                                background:"linear-gradient(to top,rgba(23,23,23,1) 0%,transparent 55%)" }} />
                  <div style={{ position:"absolute", bottom:14, left:18 }}>
                    <div className="bb" style={{ fontSize:24, letterSpacing:1 }}>{svc.title}</div>
                    <div className="dm" style={{ fontSize:11, color:"rgba(244,244,239,.5)", letterSpacing:2 }}>{svc.time}</div>
                  </div>
                  <div style={{ position:"absolute", top:12, right:12,
                                background:svc.color, color:svc.color==="transparent"?C.white:C.black,
                                padding:"3px 10px", borderRadius:2,
                                fontFamily:"'DM Sans',sans-serif", fontSize:9, fontWeight:700, letterSpacing:2 }}>
                    {svc.tag}
                  </div>
                </div>
                <div style={{ padding:20 }}>
                  <p className="dm" style={{ fontSize:13, color:"rgba(244,244,239,.55)", fontWeight:300, lineHeight:1.7, marginBottom:16 }}>
                    {svc.desc}
                  </p>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <span className="bb" style={{ fontSize:32, color:svc.color }}>
                      {svc.pricing[0].price}
                    </span>
                    <span className="dm" style={{ fontSize:10, color:C.gray, letterSpacing:2 }}>from / hatchback</span>
                  </div>
                  <motion.button whileHover={{ scale:1.04 }} whileTap={{ scale:.95 }}
                    onClick={() => navigate("/pricing")}
                    style={{ marginTop:14, width:"100%", background:svc.color,
                             color: svc.color==="#aaa" ? C.black : C.black,
                             padding:"12px 0", fontSize:10, letterSpacing:4,
                             fontFamily:"'DM Sans',sans-serif", fontWeight:700,
                             borderRadius:4, textTransform:"uppercase", border:"none", cursor:"pointer" }}>
                    See Pricing
                  </motion.button>
                </div>
              </motion.div>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={.3}>
          <div style={{ textAlign:"center", marginTop:44 }}>
            <motion.button whileHover={{ scale:1.05 }} whileTap={{ scale:.95 }}
              onClick={() => navigate("/services")}
              style={{ background:"none", border:`1px solid ${C.accent}`, color:C.accent,
                       padding:"14px 44px", fontSize:11, letterSpacing:4,
                       fontFamily:"'DM Sans',sans-serif", borderRadius:2, textTransform:"uppercase", cursor:"pointer" }}>
              View All Services →
            </motion.button>
          </div>
        </FadeIn>
      </section>

      {/* REVIEWS PREVIEW */}
      <section className="sec" style={{ padding:"80px 52px" }}>
        <FadeIn>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <p className="dm" style={{ fontSize:10, letterSpacing:6, color:C.accent, textTransform:"uppercase", marginBottom:12 }}>Customer Love</p>
            <h2 className="bb" style={{ fontSize:"clamp(42px,6vw,78px)", letterSpacing:2 }}>WHAT THEY SAY</h2>
          </div>
        </FadeIn>
        <div style={{ overflow:"hidden", maxWidth:1100, margin:"0 auto" }}>
          <AnimatePresence mode="wait">
            <motion.div key={reviewIdx}
              initial={{ opacity:0, x:60 }} animate={{ opacity:1, x:0 }}
              exit={{ opacity:0, x:-60 }} transition={{ duration:.5, ease:[.22,1,.36,1] }}
              style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:18 }}>
              {[0,1,2].map(off => {
                const rv = REVIEWS[(reviewIdx+off)%REVIEWS.length];
                return (
                  <motion.div key={rv.name+off} initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }}
                    transition={{ delay:off*.08 }}
                    style={{ background:C.steel, border:`1px solid ${C.line}`, borderRadius:8,
                             padding:"24px 22px", position:"relative", overflow:"hidden" }}>
                    <div className="bb" style={{ position:"absolute", top:10, right:16,
                                                  fontSize:70, color:"rgba(232,200,74,.05)", userSelect:"none" }}>"</div>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
                      <Stars rating={rv.rating} size={14} />
                      <span className="bb" style={{ fontSize:20, color:C.accent }}>{rv.rating}</span>
                    </div>
                    <p className="dm" style={{ fontSize:13, color:"rgba(244,244,239,.7)", lineHeight:1.8,
                                               fontWeight:300, fontStyle:"italic", marginBottom:16 }}>
                      "{rv.text}"
                    </p>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{ width:38, height:38, borderRadius:"50%", background:`linear-gradient(135deg,${C.accent},${C.accentD})`,
                                    display:"flex", alignItems:"center", justifyContent:"center",
                                    fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:12, color:C.black }}>
                        {rv.avatar}
                      </div>
                      <div>
                        <div className="dm" style={{ fontSize:13, color:C.white, fontWeight:600 }}>{rv.name}</div>
                        <div className="dm" style={{ fontSize:10, color:C.gray }}>{rv.car}</div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
          <div style={{ display:"flex", justifyContent:"center", gap:8, marginTop:28 }}>
            {REVIEWS.map((_,i) => (
              <motion.button key={i}
                onClick={() => { clearInterval(reviewTimer.current); setReviewIdx(i); }}
                animate={{ width:i===reviewIdx?28:8, background:i===reviewIdx?C.accent:C.line }}
                style={{ height:8, borderRadius:4, border:"none", cursor:"pointer", padding:0 }} />
            ))}
          </div>
        </div>
        <FadeIn delay={.2}>
          <div style={{ textAlign:"center", marginTop:36 }}>
            <motion.button whileHover={{ scale:1.05 }} whileTap={{ scale:.95 }}
              onClick={() => navigate("/reviews")}
              style={{ background:"none", border:`1px solid ${C.accent}`, color:C.accent,
                       padding:"12px 36px", fontSize:11, letterSpacing:4,
                       fontFamily:"'DM Sans',sans-serif", borderRadius:2, textTransform:"uppercase", cursor:"pointer" }}>
              Read All Reviews →
            </motion.button>
          </div>
        </FadeIn>
      </section>

      <BookCTA />
    </>
  );
}

/* ═══════════════════════════════════════════
   PAGE: ABOUT
═══════════════════════════════════════════ */
function PageAbout() {
  const navigate = useNavigate();
  const VALUES = [
    { icon:"🎯", title:"Precision", desc:"Every panel, every crevice — treated with the same obsessive attention to detail." },
    { icon:"🌿", title:"Safe Chemistry", desc:"pH-neutral, paint-safe products only. No shortcuts, no harsh detergents." },
    { icon:"⏱", title:"Your Time", desc:"We arrive on schedule and work efficiently — because we respect your day." },
    { icon:"🤝", title:"Honesty", desc:"Transparent pricing, zero hidden charges, zero upselling pressure." },
    { icon:"🏆", title:"Excellence", desc:"Showroom-finish results, every single time — or we come back and fix it." },
    { icon:"♻️", title:"Eco-Conscious", desc:"Minimal water usage, biodegradable products. Great for your car, gentle on the planet." },
  ];
  return (
    <>
      <SEO 
        title="About Us" 
        description="Learn the story behind KnockKnock Car Care and our commitment to premium doorstep car detailing in Kolkata." 
        keywords="about knockknock car care, car wash team kolkata, best car wash kolkata" 
        url="https://www.knockknockcarcare.in/about" 
      />
      <PageBanner title="ABOUT US" sub="The story behind KnockKnock Car Care" accent="Our Story" />

      {/* STORY */}
      <section className="sec" style={{ padding:"100px 52px" }}>
        <div className="ag" style={{ maxWidth:1200, margin:"0 auto", display:"grid",
                                     gridTemplateColumns:"1fr 1fr", gap:80, alignItems:"center" }}>
          <FadeIn>
            <p className="dm" style={{ fontSize:10, letterSpacing:6, color:C.accent, textTransform:"uppercase", marginBottom:14 }}>How It Started</p>
            <h2 className="bb" style={{ fontSize:"clamp(36px,5vw,64px)", lineHeight:.95, letterSpacing:2, marginBottom:24 }}>
              BORN FROM A<br /><span style={{ color:C.accent }}>SIMPLE FRUSTRATION.</span>
            </h2>
            <p className="dm" style={{ fontSize:16, lineHeight:1.9, color:"rgba(244,244,239,.62)", fontWeight:300, marginBottom:18 }}>
              It started when our founder got tired of waiting in queue at local car washes — only to receive a mediocre result that left watermarks, missed spots, and a dull finish. If you're going to do something, do it right.
            </p>
            <p className="dm" style={{ fontSize:16, lineHeight:1.9, color:"rgba(244,244,239,.62)", fontWeight:300, marginBottom:18 }}>
              KnockKnock Car Care was built on a single promise: <strong style={{ color:C.white }}>come to the customer, not the other way around</strong>, and deliver results that genuinely impress.
            </p>
            <p className="dm" style={{ fontSize:16, lineHeight:1.9, color:"rgba(244,244,239,.62)", fontWeight:300 }}>
              Based in Kolkata, we've served hundreds of car owners — from daily hatchbacks to prized luxury sedans — and every job has been treated with the same dedication and pride.
            </p>
          </FadeIn>
          <FadeIn delay={.2} x={40}>
            <div style={{ position:"relative" }}>
              <motion.div whileHover={{ scale:1.02 }} transition={{ type:"spring" }}
                style={{ borderRadius:4, overflow:"hidden",
                         boxShadow:`20px 20px 56px rgba(0,0,0,.8), -3px -3px 0 ${C.accent}` }}>
                <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=900&q=80"
                  alt="Car detailing" style={{ width:"100%", height:480, objectFit:"cover", display:"block" }} />
              </motion.div>
              <motion.div initial={{ opacity:0, y:20 }} whileInView={{ opacity:1, y:0 }}
                transition={{ delay:.4 }} viewport={{ once:true }}
                style={{ position:"absolute", bottom:-20, left:-20, background:C.charcoal,
                         border:`1px solid ${C.accent}`, borderRadius:4, padding:"16px 20px" }}>
                <div className="bb" style={{ fontSize:28, color:C.accent }}>500+</div>
                <div className="dm" style={{ fontSize:10, color:C.gray, letterSpacing:3, textTransform:"uppercase" }}>Happy Cars</div>
              </motion.div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* VALUES */}
      <section className="sec" style={{ padding:"80px 52px", background:C.charcoal }}>
        <FadeIn>
          <div style={{ textAlign:"center", marginBottom:56 }}>
            <p className="dm" style={{ fontSize:10, letterSpacing:6, color:C.accent, textTransform:"uppercase", marginBottom:12 }}>What Drives Us</p>
            <h2 className="bb" style={{ fontSize:"clamp(40px,6vw,72px)", letterSpacing:2 }}>OUR VALUES</h2>
          </div>
        </FadeIn>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid",
                      gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", gap:20 }}>
          {VALUES.map((v,i) => (
            <FadeIn key={v.title} delay={i*.08}>
              <motion.div whileHover={{ y:-6, borderColor:`rgba(232,200,74,.35)` }}
                style={{ background:C.steel, border:`1px solid ${C.line}`, borderRadius:6,
                         padding:"28px 24px", transition:"border-color .3s" }}>
                <div style={{ fontSize:32, marginBottom:14 }}>{v.icon}</div>
                <div className="bb" style={{ fontSize:24, letterSpacing:2, marginBottom:10, color:C.accent }}>{v.title}</div>
                <p className="dm" style={{ fontSize:14, color:"rgba(244,244,239,.58)", lineHeight:1.8, fontWeight:300 }}>{v.desc}</p>
              </motion.div>
            </FadeIn>
          ))}
        </div>
      </section>

      <BookCTA />
    </>
  );
}

/* ═══════════════════════════════════════════
   PAGE: SERVICES
═══════════════════════════════════════════ */
function PageServices() {
  const navigate = useNavigate();
  return (
    <>
      <SEO 
        title="Our Services - Ceramic Coating & Detailing" 
        description="Explore our top doorstep car wash packages: Basic Wash, Elite Wash, and Premium Paint Correction & Polish in Kolkata." 
        keywords="ceramic coating kolkata, paint correction kolkata, elite car wash, professional car detailing services" 
        url="https://www.knockknockcarcare.in/services" 
      />
      <PageBanner title="SERVICES" sub="Doorstep car care — from quick washes to full paint correction" accent="What We Offer" />

      <section className="sec" style={{ padding:"100px 52px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"flex", flexDirection:"column", gap:80 }}>
          {SERVICES.map((svc, si) => (
            <FadeIn key={svc.tag} delay={.05}>
              <motion.div
                style={{ display:"grid", gridTemplateColumns: si%2===0 ? "1fr 1fr" : "1fr 1fr",
                         gap:52, alignItems:"center" }}
                className="ag">
                <motion.div whileHover={{ scale:1.02 }} transition={{ type:"spring" }}
                  style={{ borderRadius:6, overflow:"hidden", order: si%2===0?0:1,
                           boxShadow:`16px 16px 48px rgba(0,0,0,.7), ${si%2===0?"-3px -3px":"3px -3px"} 0 ${svc.color}` }}>
                  <img src={svc.img} alt={svc.title}
                    style={{ width:"100%", height:380, objectFit:"cover", display:"block", filter:"brightness(.85)" }} />
                </motion.div>
                <div style={{ order: si%2===0?1:0 }}>
                  <div style={{ display:"inline-block", background:svc.color, color:C.black,
                                padding:"5px 14px", borderRadius:2, fontFamily:"'DM Sans',sans-serif",
                                fontSize:10, fontWeight:700, letterSpacing:3, marginBottom:16 }}>{svc.tag}</div>
                  <h2 className="bb" style={{ fontSize:"clamp(32px,4vw,56px)", letterSpacing:2, lineHeight:.95, marginBottom:14 }}>
                    {svc.title}
                  </h2>
                  <p className="dm" style={{ fontSize:11, color:C.gray, letterSpacing:3, textTransform:"uppercase", marginBottom:16 }}>{svc.time}</p>
                  <p className="dm" style={{ fontSize:15, lineHeight:1.85, color:"rgba(244,244,239,.6)", fontWeight:300, marginBottom:24 }}>{svc.desc}</p>
                  <StaggerList>
                    {svc.features.map(f => (
                      <StaggerItem key={f}
                        style={{ fontSize:14, color:"rgba(244,244,239,.7)", padding:"8px 0",
                                 borderBottom:`1px solid ${C.line}`, display:"flex",
                                 alignItems:"flex-start", gap:10, fontWeight:300, lineHeight:1.5,
                                 fontFamily:"'DM Sans',sans-serif" }}>
                        <span style={{ color:svc.color, flexShrink:0 }}>✓</span>{f}
                      </StaggerItem>
                    ))}
                  </StaggerList>
                  <div style={{ marginTop:28 }}>
                    <p className="dm" style={{ fontSize:10, color:C.gray, letterSpacing:3, textTransform:"uppercase", marginBottom:12 }}>Starting From</p>
                    <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
                      {svc.pricing.map(p => (
                        <motion.div key={p.type} whileHover={{ borderColor:svc.color }}
                          style={{ background:C.panel, border:`1px solid ${C.line}`, borderRadius:4,
                                   padding:"10px 16px", transition:"border-color .3s" }}>
                          <div className="dm" style={{ fontSize:10, color:C.gray, marginBottom:4 }}>{p.type}</div>
                          <div className="bb" style={{ fontSize:26, color:svc.color }}>{p.price}</div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  <motion.button whileHover={{ scale:1.05 }} whileTap={{ scale:.95 }}
                    onClick={() => navigate("/contact")}
                    style={{ marginTop:24, background:svc.color, color:C.black,
                             padding:"14px 36px", fontSize:10, letterSpacing:4,
                             fontFamily:"'DM Sans',sans-serif", fontWeight:700,
                             borderRadius:4, border:"none", cursor:"pointer", textTransform:"uppercase" }}>
                    Book This Service
                  </motion.button>
                </div>
              </motion.div>
              {si < SERVICES.length-1 && (
                <div style={{ height:1, background:`linear-gradient(to right,transparent,${C.line},transparent)`,
                              marginTop:80 }} />
              )}
            </FadeIn>
          ))}
        </div>
      </section>
      <BookCTA />
    </>
  );
}

/* ═══════════════════════════════════════════
   PAGE: PRICING
═══════════════════════════════════════════ */
function PagePricing() {
  const navigate = useNavigate();
  const CAR_TYPES = ["Hatchback","Sedan + SUV","Large SUV"];
  const [activeCar, setActiveCar] = useState(0);
  const FAQ = [
    // { q:"Is the 25% discount automatic?", a:"Yes! For your very first booking, we automatically apply 25% off. Just book and we handle the rest." },
    { q:"Are there any hidden charges?", a:"Absolutely not. The price you see is the price you pay. No parking charges, no equipment fees — nothing extra." },
    { q:"Do I need to be home during the wash?", a:"Not necessarily. You can hand over keys to our team or leave the car accessible. We'll send you before & after photos." },
    { q:"What products do you use?", a:"All our shampoos are pH-neutral and paint-safe. We use wax shampoo for the two-bucket method and professional-grade ceramic spray for coating." },
    { q:"How long does each service take?", a:"Basic: ~30 minutes. Elite: ~1.5 hours. Paint Correction & Wax: ~3 hours. Times may vary slightly based on car size." },
    { q:"Do you service all car types?", a:"Yes — hatchbacks, sedans, SUVs, MUVs. Just select the correct car type when booking for accurate pricing." },
  ];
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      <SEO 
        title="Pricing & Packages" 
        description="Transparent pricing for our premium doorstep car wash services in Kolkata. Starting at ₹499." 
        keywords="car wash price kolkata, affordable car detailing, hatchbook sedan wash pricing" 
        url="https://www.knockknockcarcare.in/pricing" 
      />
      <PageBanner title="PRICING" sub="Transparent pricing. Zero surprises." accent="What It Costs" />

      {/* CAR TYPE TOGGLE */}
      <section className="sec" style={{ padding:"80px 52px" }}>
        <FadeIn>
          <div style={{ textAlign:"center", marginBottom:40 }}>
            <p className="dm" style={{ fontSize:10, letterSpacing:6, color:C.gray, textTransform:"uppercase", marginBottom:16 }}>Select Your Car Type</p>
            <div style={{ display:"inline-flex", background:C.steel, border:`1px solid ${C.line}`, borderRadius:6, padding:4, gap:4 }}>
              {CAR_TYPES.map((t,i) => (
                <motion.button key={t} onClick={() => setActiveCar(i)}
                  animate={{ background: i===activeCar ? C.accent : "transparent",
                             color: i===activeCar ? C.black : C.gray }}
                  transition={{ duration:.25 }}
                  style={{ padding:"10px 22px", borderRadius:4, border:"none", cursor:"pointer",
                           fontFamily:"'DM Sans',sans-serif", fontSize:12, fontWeight:600, letterSpacing:1 }}>
                  {t}
                </motion.button>
              ))}
            </div>
          </div>
        </FadeIn>

        <div style={{ maxWidth:960, margin:"0 auto", display:"grid",
                      gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:22 }}>
          {SERVICES.map((svc, si) => (
            <FadeIn key={svc.tag} delay={si*.1}>
              <motion.div whileHover={{ y:-10, boxShadow:`0 24px 60px rgba(0,0,0,.6)` }}
                transition={{ type:"spring", stiffness:200 }}
                style={{ background:C.steel, border:`1px solid ${C.line}`, borderRadius:8,
                         overflow:"hidden", boxShadow:"0 6px 32px rgba(0,0,0,.4)",
                         ...(si===1 ? { border:`1px solid ${C.blue}`, position:"relative" } : {}) }}>
                {si===1 && (
                  <div style={{ background:C.blue, color:C.black, textAlign:"center",
                                padding:"6px", fontFamily:"'DM Sans',sans-serif",
                                fontSize:10, fontWeight:700, letterSpacing:3 }}>
                    ⭐ MOST POPULAR
                  </div>
                )}
                <div style={{ padding:"28px 24px 0" }}>
                  <div style={{ display:"inline-block", background:svc.color, color:C.black,
                                padding:"4px 12px", borderRadius:2,
                                fontFamily:"'DM Sans',sans-serif", fontSize:9, fontWeight:700, letterSpacing:3 }}>
                    {svc.tag}
                  </div>
                  <h3 className="bb" style={{ fontSize:28, letterSpacing:1, marginTop:12, marginBottom:4 }}>{svc.title}</h3>
                  <p className="dm" style={{ fontSize:11, color:C.gray, letterSpacing:2, marginBottom:20 }}>{svc.time}</p>

                  {/* Big price */}
                  <AnimatePresence mode="wait">
                    <motion.div key={activeCar}
                      initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-12 }}
                      transition={{ duration:.3 }}>
                      <div className="bb" style={{ fontSize:58, color:svc.color, lineHeight:1 }}>
                        {svc.pricing[activeCar].price}
                      </div>
                      <p className="dm" style={{ fontSize:11, color:C.gray, letterSpacing:2, marginBottom:20 }}>
                        for {CAR_TYPES[activeCar]}
                      </p>
                    </motion.div>
                  </AnimatePresence>

                  <div style={{ height:1, background:C.line, marginBottom:18 }} />
                  <StaggerList>
                    {svc.features.slice(0,5).map(f => (
                      <StaggerItem key={f}
                        style={{ fontSize:13, color:"rgba(244,244,239,.65)", padding:"6px 0",
                                 borderBottom:`1px solid ${C.line}`, display:"flex",
                                 alignItems:"flex-start", gap:9, fontWeight:300,
                                 fontFamily:"'DM Sans',sans-serif" }}>
                        <span style={{ color:svc.color, flexShrink:0 }}>✓</span>{f}
                      </StaggerItem>
                    ))}
                    {svc.features.length > 5 && (
                      <li style={{ fontSize:12, color:C.gray, padding:"6px 0",
                                   fontFamily:"'DM Sans',sans-serif" }}>
                        + {svc.features.length-5} more inclusions
                      </li>
                    )}
                  </StaggerList>
                </div>

                <div style={{ padding:24 }}>
                  {/* <motion.div
                    animate={{ boxShadow:["0 0 0px rgba(232,200,74,0)","0 0 10px rgba(232,200,74,.2)","0 0 0px rgba(232,200,74,0)"] }}
                    transition={{ repeat:Infinity, duration:2.5 }}
                    style={{ marginBottom:14, padding:"10px 14px", background:"rgba(232,200,74,.07)",
                             border:`1px solid rgba(232,200,74,.2)`, borderRadius:4,
                             display:"flex", alignItems:"center", gap:8 }}>
                    <span>🎉</span>
                    <span className="dm" style={{ fontSize:12, color:C.accent, fontWeight:600 }}>Your car our service</span>
                  </motion.div> */}
                  <motion.button whileHover={{ scale:1.04 }} whileTap={{ scale:.95 }}
                    onClick={() => navigate("/contact")}
                    style={{ width:"100%", background:svc.color, color:C.black,
                             padding:"14px", fontSize:10, letterSpacing:4,
                             fontFamily:"'DM Sans',sans-serif", fontWeight:700,
                             borderRadius:4, border:"none", cursor:"pointer", textTransform:"uppercase" }}>
                    Book Now
                  </motion.button>
                </div>
              </motion.div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="sec" style={{ padding:"40px 52px 100px" }}>
        <FadeIn>
          <div style={{ textAlign:"center", marginBottom:52 }}>
            <p className="dm" style={{ fontSize:10, letterSpacing:6, color:C.accent, textTransform:"uppercase", marginBottom:12 }}>Have Questions?</p>
            <h2 className="bb" style={{ fontSize:"clamp(38px,5vw,68px)", letterSpacing:2 }}>FAQ</h2>
          </div>
        </FadeIn>
        <div style={{ maxWidth:800, margin:"0 auto", display:"flex", flexDirection:"column", gap:10 }}>
          {FAQ.map((f,i) => (
            <FadeIn key={i} delay={i*.06}>
              <motion.div
                style={{ background:C.steel, border:`1px solid ${openFaq===i ? C.accent : C.line}`,
                         borderRadius:6, overflow:"hidden", transition:"border-color .3s" }}>
                <button onClick={() => setOpenFaq(openFaq===i?null:i)}
                  style={{ width:"100%", background:"none", border:"none", cursor:"pointer",
                           padding:"18px 22px", display:"flex", justifyContent:"space-between",
                           alignItems:"center", gap:16 }}>
                  <span className="dm" style={{ fontSize:15, color:C.white, fontWeight:600, textAlign:"left" }}>{f.q}</span>
                  <motion.span animate={{ rotate:openFaq===i?180:0 }}
                    style={{ color:C.accent, fontSize:18, flexShrink:0 }}>▾</motion.span>
                </button>
                <AnimatePresence>
                  {openFaq===i && (
                    <motion.div initial={{ height:0 }} animate={{ height:"auto" }} exit={{ height:0 }}
                      transition={{ duration:.35, ease:[.22,1,.36,1] }}
                      style={{ overflow:"hidden" }}>
                      <p className="dm" style={{ padding:"0 22px 18px", fontSize:14, color:"rgba(244,244,239,.6)",
                                                 lineHeight:1.8, fontWeight:300 }}>{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </FadeIn>
          ))}
        </div>
      </section>
      <BookCTA />
    </>
  );
}

/* ═══════════════════════════════════════════
   PAGE: GALLERY
═══════════════════════════════════════════ */
function PageGallery() {
  const [lightbox, setLightbox] = useState(null);
  return (
    <>
      <SEO 
        title="Our Work Gallery" 
        description="View stunning before and after results of our doorstep foam washes, interior cleaning, and paint correction in Kolkata." 
        keywords="car wash gallery, car detailing photos, clean car images, best car detailing kolkata" 
        url="https://www.knockknockcarcare.in/gallery" 
      />
      <PageBanner title="GALLERY" sub="Every wash tells a story. Here's ours." accent="Our Work" />

      <section className="sec" style={{ padding:"80px 52px 100px" }}>
        <motion.div
          initial="hidden" animate="show"
          variants={{ hidden:{}, show:{ transition:{ staggerChildren:.06 }}}}
          style={{ maxWidth:1200, margin:"0 auto", display:"grid",
                   gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:14 }}>
          {GALLERY.map((img,i) => (
            <motion.div key={i}
              variants={{ hidden:{ opacity:0, scale:.88, y:20 }, show:{ opacity:1, scale:1, y:0, transition:{ duration:.6, ease:[.22,1,.36,1] }}}}
              whileHover={{ scale:1.03, zIndex:2 }}
              onClick={() => setLightbox(img)}
              style={{ position:"relative", borderRadius:6, overflow:"hidden", cursor:"pointer", aspectRatio:"4/3" }}>
              <img src={img.src} alt={img.label}
                style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }} />
              <motion.div initial={{ opacity:0 }} whileHover={{ opacity:1 }}
                style={{ position:"absolute", inset:0, display:"flex", alignItems:"flex-end", padding:18,
                         background:"linear-gradient(to top,rgba(7,7,7,.85) 0%,transparent 65%)" }}>
                <span className="bb" style={{ fontSize:20, letterSpacing:2 }}>{img.label}</span>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={() => setLightbox(null)}
            style={{ position:"fixed", inset:0, zIndex:200, background:"rgba(0,0,0,.96)",
                     display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
            <motion.img
              initial={{ scale:.78, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:.78, opacity:0 }}
              transition={{ type:"spring", damping:20 }}
              src={lightbox.src} alt={lightbox.label}
              style={{ maxWidth:"90vw", maxHeight:"88vh", objectFit:"contain", borderRadius:4 }}
              onClick={e => e.stopPropagation()} />
            <motion.button whileHover={{ scale:1.15, rotate:90 }} onClick={() => setLightbox(null)}
              style={{ position:"absolute", top:20, right:20, background:C.accent, color:C.black,
                       width:42, height:42, borderRadius:"50%", fontSize:22, fontWeight:700,
                       border:"none", cursor:"pointer" }}>×</motion.button>
            <div className="bb" style={{ position:"absolute", bottom:28, left:"50%",
                                         transform:"translateX(-50%)", fontSize:18, letterSpacing:3, color:C.gray }}>
              {lightbox.label}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/* ═══════════════════════════════════════════
   PAGE: REVIEWS
═══════════════════════════════════════════ */
function PageReviews() {
  const navigate = useNavigate();
  return (
    <>
      <SEO 
        title="Customer Reviews" 
        description="Read what car owners in Kolkata are saying about KnockKnock Car Care's premium doorstep detailing services." 
        keywords="knockknock car care reviews, car wash ratings kolkata, trusted car wash kolkata" 
        url="https://www.knockknockcarcare.in/reviews" 
      />
      <PageBanner title="REVIEWS" sub="Real customers, real results" accent="Customer Love" />

      {/* Aggregate ratings */}
      <section className="sec" style={{ padding:"80px 52px 40px" }}>
        <FadeIn>
          <div style={{ display:"flex", justifyContent:"center", gap:20, flexWrap:"wrap", marginBottom:64 }}>
            {[{ plat:"Google", rating:4.9, color:"#fbbc04" }, { plat:"Facebook", rating:4.8, color:"#4267B2" }, { plat:"Overall", rating:5.0, color:C.accent }].map(({ plat, rating, color }) => (
              <motion.div key={plat} whileHover={{ y:-6, borderColor:color, scale:1.03 }}
                style={{ border:`1px solid ${C.line}`, borderRadius:8, padding:"24px 36px",
                         textAlign:"center", transition:"border-color .3s, transform .3s",
                         background:C.steel, minWidth:160 }}>
                <span className="dm" style={{ fontSize:10, letterSpacing:4, color:C.gray, textTransform:"uppercase" }}>{plat}</span>
                <div className="bb" style={{ fontSize:56, color, lineHeight:1.1, margin:"8px 0" }}>{rating}</div>
                <Stars rating={rating} color={color} size={16} />
                <div className="dm" style={{ fontSize:11, color:C.gray, marginTop:8 }}>out of 5.0</div>
              </motion.div>
            ))}
          </div>
        </FadeIn>

        {/* All review cards */}
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid",
                      gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:20 }}>
          {REVIEWS.map((rv, i) => (
            <FadeIn key={rv.name} delay={i*.08}>
              <motion.div whileHover={{ y:-8, borderColor:`rgba(232,200,74,.3)` }}
                transition={{ type:"spring", stiffness:200 }}
                style={{ background:C.steel, border:`1px solid ${C.line}`, borderRadius:8,
                         padding:"28px 24px", position:"relative", overflow:"hidden",
                         boxShadow:"0 4px 24px rgba(0,0,0,.4)", transition:"border-color .3s" }}>
                <div className="bb" style={{ position:"absolute", top:12, right:18,
                                              fontSize:80, color:"rgba(232,200,74,.05)", userSelect:"none" }}>"</div>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
                  <Stars rating={rv.rating} size={15} />
                  <span className="bb" style={{ fontSize:24, color:C.accent }}>{rv.rating}</span>
                </div>
                <p className="dm" style={{ fontSize:14, color:"rgba(244,244,239,.72)", lineHeight:1.85,
                                           fontWeight:300, fontStyle:"italic", marginBottom:20 }}>
                  "{rv.text}"
                </p>
                <div style={{ height:1, background:C.line, marginBottom:16 }} />
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:44, height:44, borderRadius:"50%", flexShrink:0,
                                background:`linear-gradient(135deg,${C.accent},${C.accentD})`,
                                display:"flex", alignItems:"center", justifyContent:"center",
                                fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:13, color:C.black }}>
                    {rv.avatar}
                  </div>
                  <div>
                    <div className="dm" style={{ fontSize:14, color:C.white, fontWeight:600 }}>{rv.name}</div>
                    <div className="dm" style={{ fontSize:11, color:C.gray }}>{rv.location}</div>
                  </div>
                  <div style={{ marginLeft:"auto" }}>
                    <div className="dm" style={{ fontSize:10, color:C.gray, textAlign:"right" }}>Car</div>
                    <div className="dm" style={{ fontSize:11, color:`rgba(232,200,74,.7)`, fontWeight:600 }}>{rv.car}</div>
                  </div>
                </div>
              </motion.div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Leave a review CTA */}
      <FadeIn>
        <div className="sec" style={{ padding:"60px 52px 100px", textAlign:"center" }}>
          <div style={{ maxWidth:600, margin:"0 auto" }}>
            <h3 className="bb" style={{ fontSize:"clamp(32px,5vw,56px)", letterSpacing:2, marginBottom:14 }}>
              LOVED YOUR WASH?
            </h3>
            <p className="dm" style={{ fontSize:15, color:C.gray, fontWeight:300, marginBottom:28 }}>
              Share your experience on Google or WhatsApp — it helps other car owners find us!
            </p>
            <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap" }}>
              <motion.a href="https://g.page/r/review" target="_blank" rel="noreferrer"
                whileHover={{ scale:1.06 }} whileTap={{ scale:.95 }}
                style={{ background:C.accent, color:C.black, padding:"14px 32px",
                         fontSize:11, letterSpacing:3, fontFamily:"'DM Sans',sans-serif",
                         fontWeight:700, borderRadius:2, textTransform:"uppercase" }}>
                ⭐ Review on Google
              </motion.a>
              <motion.a href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent("Hi! I wanted to share my feedback about my recent car wash with KnockKnock Car Care 😊")}`}
                target="_blank" rel="noreferrer"
                whileHover={{ scale:1.06 }} whileTap={{ scale:.95 }}
                style={{ background:C.wa, color:"#fff", padding:"14px 32px",
                         fontSize:11, letterSpacing:3, fontFamily:"'DM Sans',sans-serif",
                         fontWeight:700, borderRadius:2, textTransform:"uppercase",
                         display:"flex", alignItems:"center", gap:8 }}>
                💬 Share on WhatsApp
              </motion.a>
            </div>
          </div>
        </div>
      </FadeIn>
    </>
  );
}

/* ═══════════════════════════════════════════
   PAGE: CONTACT
═══════════════════════════════════════════ */
function PageContact() {
  const [form, setForm] = useState({ name:"", phone:"", vehicle:"", service:"", message:"" });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = e => {
    e.preventDefault();
    const msg = encodeURIComponent(
      `Hi! I'd like to book a car wash.\n\nName: ${form.name}\nPhone: ${form.phone}\nVehicle: ${form.vehicle}\nService: ${form.service}\nMessage: ${form.message}`
    );
    window.open(`https://wa.me/${WA_NUMBER}?text=${msg}`, "_blank");
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
    setForm({ name:"", phone:"", vehicle:"", service:"", message:"" });
  };

  return (
    <>
      <SEO 
        title="Contact & Booking" 
        description="Book your premium doorstep car wash today. Call us or WhatsApp instantly to schedule your appointment in Kolkata." 
        keywords="book car wash kolkata, contact knockknock car care, schedule car detailing" 
        url="https://www.knockknockcarcare.in/contact" 
      />
      <PageBanner title="CONTACT" sub="Book your wash or ask us anything" accent="Get In Touch" />

      <section className="sec" style={{ padding:"80px 52px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto", display:"grid",
                      gridTemplateColumns:"1fr 1fr", gap:60, alignItems:"start" }}
          className="ag">

          {/* Info */}
          <FadeIn>
            <h2 className="bb" style={{ fontSize:"clamp(34px,4vw,58px)", letterSpacing:2, lineHeight:.95, marginBottom:24 }}>
              LET'S GET<br /><span style={{ color:C.accent }}>YOUR CAR</span><br />SHINING.
            </h2>
            <p className="dm" style={{ fontSize:15, color:"rgba(244,244,239,.6)", fontWeight:300, lineHeight:1.85, marginBottom:36 }}>
              Fill in the form and it'll open directly in WhatsApp — so we can confirm your booking instantly. Or just call or message us directly.
            </p>

            {[
              { icon:"📞", label:"Call / WhatsApp", val:"9641915892", href:"tel:9641915892", col:C.accent },
              { icon:"💬", label:"WhatsApp Chat", val:"Chat Instantly", href:`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`, col:C.wa },
              { icon:"🌐", label:"Website", val:"knockknockcarcare.in", href:"https://www.knockknockcarcare.in", col:C.accent },
              { icon:"📍", label:"Location", val:"Kolkata, West Bengal", href:"https://maps.google.com", col:C.accent },
            ].map(c => (
              <motion.a key={c.val} href={c.href} target="_blank" rel="noreferrer"
                whileHover={{ x:6, borderColor:c.col }}
                style={{ display:"flex", alignItems:"center", gap:16, padding:"16px 18px",
                         border:`1px solid ${C.line}`, borderRadius:6, marginBottom:10,
                         transition:"border-color .3s, transform .3s" }}>
                <div style={{ width:44, height:44, borderRadius:4, flexShrink:0,
                              background:"rgba(232,200,74,.07)", border:`1px solid rgba(232,200,74,.15)`,
                              display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>{c.icon}</div>
                <div>
                  <div className="dm" style={{ fontSize:9, color:C.gray, letterSpacing:3, textTransform:"uppercase", marginBottom:3 }}>{c.label}</div>
                  <div className="dm" style={{ fontSize:14, color:c.col, fontWeight:600 }}>{c.val}</div>
                </div>
              </motion.a>
            ))}

            {/* social */}
            <div style={{ marginTop:28 }}>
              <p className="dm" style={{ fontSize:9, color:C.gray, letterSpacing:4, textTransform:"uppercase", marginBottom:14 }}>Follow Us</p>
              <div style={{ display:"flex", gap:10 }}>
                {SOCIAL.map(s => (
                  <motion.a key={s.label} href={s.href} target="_blank" rel="noreferrer"
                    whileHover={{ scale:1.15, backgroundColor:s.color, borderColor:s.color, color:"#fff" }}
                    title={s.label}
                    style={{ width:40, height:40, borderRadius:4, display:"flex", alignItems:"center",
                             justifyContent:"center", border:`1px solid ${C.line}`, color:C.gray, transition:"all .25s" }}>
                    {s.icon}
                  </motion.a>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Form */}
          <FadeIn delay={.15} x={30}>
            <form onSubmit={handleSubmit}
              style={{ background:C.steel, border:`1px solid ${C.line}`, borderRadius:8, padding:"36px 32px" }}>
              <h3 className="bb" style={{ fontSize:28, letterSpacing:2, marginBottom:24, color:C.accent }}>BOOK YOUR WASH</h3>
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
                  <div>
                    <label className="dm" style={{ fontSize:10, letterSpacing:3, color:C.gray, textTransform:"uppercase", display:"block", marginBottom:7 }}>Your Name *</label>
                    <input required placeholder="Rahul Sharma" value={form.name} onChange={e => setForm({...form,name:e.target.value})} />
                  </div>
                  <div>
                    <label className="dm" style={{ fontSize:10, letterSpacing:3, color:C.gray, textTransform:"uppercase", display:"block", marginBottom:7 }}>Phone *</label>
                    <input required placeholder="9876543210" value={form.phone} onChange={e => setForm({...form,phone:e.target.value})} />
                  </div>
                </div>
                <div>
                  <label className="dm" style={{ fontSize:10, letterSpacing:3, color:C.gray, textTransform:"uppercase", display:"block", marginBottom:7 }}>Vehicle</label>
                  <input placeholder="e.g. Hyundai Creta (SUV)" value={form.vehicle} onChange={e => setForm({...form,vehicle:e.target.value})} />
                </div>
                <div>
                  <label className="dm" style={{ fontSize:10, letterSpacing:3, color:C.gray, textTransform:"uppercase", display:"block", marginBottom:7 }}>Service</label>
                  <select value={form.service} onChange={e => setForm({...form,service:e.target.value})}>
                    <option value="">Select a service…</option>
                    <option>Basic Car Wash</option>
                    <option>Elite Car Wash (Deep Cleaning)</option>
                    <option>Paint Correction & Wax Polish</option>
                  </select>
                </div>
                <div>
                  <label className="dm" style={{ fontSize:10, letterSpacing:3, color:C.gray, textTransform:"uppercase", display:"block", marginBottom:7 }}>Message / Preferred Time</label>
                  <textarea placeholder="Any details, preferred date/time, location…" value={form.message} onChange={e => setForm({...form,message:e.target.value})} />
                </div>
                <AnimatePresence mode="wait">
                  {submitted ? (
                    <motion.div key="ok" initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                      style={{ background:"rgba(37,211,102,.12)", border:`1px solid ${C.wa}`,
                               borderRadius:4, padding:"14px 18px", textAlign:"center" }}>
                      <span className="dm" style={{ color:C.wa, fontWeight:600 }}>✓ Opening WhatsApp with your details!</span>
                    </motion.div>
                  ) : (
                    <motion.button key="btn" type="submit" whileHover={{ scale:1.04 }} whileTap={{ scale:.95 }}
                      style={{ background:C.wa, color:"#fff", padding:"16px",
                               fontSize:11, letterSpacing:4, fontFamily:"'DM Sans',sans-serif",
                               fontWeight:700, borderRadius:4, border:"none", cursor:"pointer",
                               textTransform:"uppercase", display:"flex", alignItems:"center",
                               justifyContent:"center", gap:10 }}>
                      <svg viewBox="0 0 32 32" fill="white" width="16" height="16">
                        <path d="M16 3C9.373 3 4 8.373 4 15c0 2.385.676 4.612 1.843 6.504L4 29l7.697-1.82A11.93 11.93 0 0016 27c6.627 0 12-5.373 12-12S22.627 3 16 3z"/>
                      </svg>
                      Send via WhatsApp
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </form>
          </FadeIn>
        </div>
      </section>

      {/* Map */}
      <section className="sec" style={{ padding:"0 52px 100px" }}>
        <FadeIn>
          <div style={{ maxWidth:1100, margin:"0 auto", position:"relative" }}>
            <motion.div initial={{ scale:0, rotate:-10 }} whileInView={{ scale:1, rotate:0 }}
              transition={{ type:"spring", damping:14, delay:.3 }} viewport={{ once:true }}
              style={{ position:"absolute", top:-14, right:20, zIndex:5,
                       background:C.accent, color:C.black, padding:"5px 16px", borderRadius:2,
                       fontFamily:"'DM Sans',sans-serif", fontSize:10, fontWeight:700, letterSpacing:3 }}>
              📍 We're Here
            </motion.div>
            <div style={{ borderRadius:8, overflow:"hidden", boxShadow:`0 8px 40px rgba(0,0,0,.6), 3px 3px 0 ${C.accent}` }}>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d117889.43418025941!2d88.3618451906492!3d22.57742701556627!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a02750032325635%3A0xa64265942550b703!2sKnock%20knock%20Car%20Care!5e0!3m2!1sen!2sin&4v1773730204991"
                width="100%" height="400"
                style={{ border:0, display:"block", filter:"invert(92%) hue-rotate(180deg) brightness(.88) contrast(1.1) saturate(.7)" }}
                allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                title="KnockKnock Car Care Location" />
            </div>
          </div>
        </FadeIn>
      </section>
    </>
  );
}

/* ═══════════════════════════════════════════
   SCROLL TO TOP ON ROUTE CHANGE
═══════════════════════════════════════════ */
function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0,0); }, [pathname]);
  return null;
}

/* ═══════════════════════════════════════════
   PAGE TRANSITION WRAPPER
═══════════════════════════════════════════ */
function PageTransition({ children }) {
  const { pathname } = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div key={pathname}
        initial={{ opacity:0, y:18 }}
        animate={{ opacity:1, y:0 }}
        exit={{ opacity:0, y:-18 }}
        transition={{ duration:.45, ease:[.22,1,.36,1] }}>
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/* ═══════════════════════════════════════════
   GLOBAL STYLES
═══════════════════════════════════════════ */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,wght@0,300;0,400;0,600;1,300&display=swap');
    *,*::before,*::after{margin:0;padding:0;box-sizing:border-box}
    html{scroll-behavior:smooth}
    ::-webkit-scrollbar{width:3px}
    ::-webkit-scrollbar-track{background:${C.black}}
    ::-webkit-scrollbar-thumb{background:${C.accent};border-radius:2px}
    .bb{font-family:'Bebas Neue',cursive}
    .dm{font-family:'DM Sans',sans-serif}
    a{color:inherit;text-decoration:none}
    button{cursor:pointer;border:none;outline:none}
    input,textarea,select{
      width:100%;background:${C.steel};
      border:1px solid ${C.line};border-radius:4px;
      padding:14px 16px;color:${C.white};
      font-family:'DM Sans',sans-serif;font-size:15px;
      outline:none;transition:border-color .3s,box-shadow .3s;appearance:none;
    }
    input:focus,textarea:focus,select:focus{
      border-color:${C.accent};box-shadow:0 0 0 3px rgba(232,200,74,.12);
    }
    textarea{resize:vertical;min-height:110px}
    select option{background:${C.steel};color:${C.white}}
    .nb{
      background:none;color:${C.white};
      font-family:'DM Sans',sans-serif;font-size:10px;
      letter-spacing:3px;text-transform:uppercase;
      opacity:.7;transition:all .3s;
      border-bottom:1px solid transparent;padding:3px 0;
    }
    .nb:hover{opacity:1;color:${C.accent};border-bottom-color:${C.accent}}
    @media(max-width:768px){
      .dl{display:none!important}.hm{display:flex!important}
      .ag,.sg,.fg,.cr{grid-template-columns:1fr!important}
      nav{padding:14px 20px!important}
      .sec{padding-left:20px!important;padding-right:20px!important}
      footer{padding-left:20px!important;padding-right:20px!important}
    }
    @media(min-width:769px){.hm{display:none!important}}
  `}</style>
);

/* ═══════════════════════════════════════════
   APP ROOT
═══════════════════════════════════════════ */
export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <div style={{ background:C.black, color:C.white, fontFamily:"Georgia,serif", overflowX:"hidden" }}>
          <GlobalStyles />
          <CursorGlow />
          <FloatingWA />
          <Nav />
          <ScrollTop />
          <PageTransition>
            <Routes>
              <Route path="/"         element={<PageHome />} />
              <Route path="/about"    element={<PageAbout />} />
              <Route path="/services" element={<PageServices />} />
              <Route path="/pricing"  element={<PagePricing />} />
              <Route path="/gallery"  element={<PageGallery />} />
              <Route path="/reviews"  element={<PageReviews />} />
              <Route path="/contact"  element={<PageContact />} />
            </Routes>
          </PageTransition>
          <Footer />
        </div>
      </BrowserRouter>
    </HelmetProvider>
  );
}